<template>
    <div class="sol">
        <div class="sol-main">
            <header class="sol-project-name-bar">
                <span class="sol-project-name-icon" aria-hidden="true">
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                        stroke-width="1.65">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <path d="M14 2v6h6" />
                        <path d="M8 13h8M8 17h8" />
                    </svg>
                </span>
                <h1 class="sol-project-name-title">{{ currentProject.name || '当前项目' }}</h1>
            </header>

            <div v-if="!currentUser" class="sol-panel">
                <div class="sol-list-toolbar">
                    <el-form :inline="true" class="sol-filter-bar" @submit.native.prevent>
                        <el-form-item>
                            <el-select v-model="filterSpace" size="small" clearable placeholder="全部所属空间"
                                style="width: 220px" @change="onFilterChange">
                                <el-option label="全部所属空间" value="" />
                                <el-option v-for="sp in spaceOptions" :key="sp.spaceId" :label="sp.spaceName"
                                    :value="String(sp.spaceId)" />
                            </el-select>
                        </el-form-item>
                        <el-form-item>
                            <el-select v-model="filterCount" size="small" clearable placeholder="全部日志数量"
                                style="width: 160px" @change="onFilterChange">
                                <el-option label="全部日志数量" value="" />
                                <el-option label="1–5 条" value="1-5" />
                                <el-option label="6–10 条" value="6-10" />
                                <el-option label="11 条及以上" value="11+" />
                            </el-select>
                        </el-form-item>
                        <el-form-item>
                            <el-input v-model.trim="filterSearch" size="small" clearable placeholder="搜索姓名、所属空间…"
                                suffix-icon="el-icon-search" style="width: 220px" @input="onSearchInput"
                                @clear="onFilterChange" />
                        </el-form-item>
                    </el-form>
                    <el-button type="primary" size="small" icon="el-icon-download" :loading="exporting"
                        @click="exportList">导出列表</el-button>
                </div>
                <div class="sol-panel-body" v-loading="listLoading">
                    <el-table :data="logs" stripe style="width: 100%">
                        <el-table-column type="index" label="序号" width="80" align="center" :index="listIndexMethod" />
                        <el-table-column prop="userName" label="姓名" width="180" min-width="180" show-overflow-tooltip />
                        <el-table-column prop="spaceName" label="所属空间" align="center" show-overflow-tooltip />
                        <el-table-column label="日志数量" width="120" align="center">
                            <template slot-scope="{ row }">{{ row.logCount }} 条</template>
                        </el-table-column>
                        <el-table-column label="操作" width="120" align="center">
                            <template slot-scope="{ row }">
                                <el-button type="text" size="small" @click="showVideos(row)">查看</el-button>
                            </template>
                        </el-table-column>
                        <template slot="empty">
                            <el-empty description="暂无数据" />
                        </template>
                    </el-table>
                </div>
                <div v-show="listTotal" class="sol-pagination">
                    <span class="sol-pagination-info">共 {{ listTotal }} 条</span>
                    <pagination :total="listTotal" :page.sync="listPage" :limit.sync="listPageSize"
                        :pageSizes="[10, 20, 30]" @pagination="onListPageChange" />
                </div>
            </div>

            <div v-else class="sol-panel">
                <div class="sol-panel-header sol-detail-header">
                    <el-button size="small" icon="el-icon-arrow-left" @click="currentUser = null">返回</el-button>
                    <div class="sol-panel-title">{{ currentUser.userName }}</div>
                    <div class="sol-detail-meta">
                        <span>所属空间：<strong>{{ currentUser.spaceName }}</strong></span>
                        <span>日志数量：<strong>{{ currentUser.logCount }} 条</strong></span>
                    </div>
                </div>
                <div class="sol-panel-body" v-loading="videoLoading">
                    <!-- key：强制重建表格，避免 Element UI 列 store 与 scopedSlots 脱节导致单元格空白 -->
                    <el-table :key="videoTableKey" :data="currentVideos" stripe style="width: 100%">
                        <el-table-column type="index" label="序号" width="64" align="center"
                            :index="videoIndexMethod"></el-table-column>
                        <el-table-column label="录屏名称" min-width="280">
                            <template slot-scope="{ row }">
                                <div class="sol-video-name-cell">
                                    <div class="sol-video-cover">
                                        <span class="sol-video-thumb" @click="openPreview(row)">
                                            <video v-if="getVideoSrc(row) && !isCoverFailed(row)" :key="row.identifier"
                                                class="sol-cover-video" :src="getVideoSrc(row)" muted playsinline
                                                preload="metadata" @loadedmetadata="seekCoverFrame($event, row)"
                                                @error="markCoverFailed(row)" />
                                            <span v-else aria-hidden="true">▶</span>
                                        </span>
                                        <div class="sol-cover-resolution">{{ row.resolution || '—' }}</div>
                                    </div>
                                    <div class="sol-video-name-info" :title="row.title || '—'">
                                        <el-button type="text" size="small" @click="openPreview(row)">
                                            <span class="sol-name-line">{{ videoNameLines(row.title).line1 }}</span>
                                            <span v-if="videoNameLines(row.title).line2" class="sol-name-line">{{
                                                videoNameLines(row.title).line2 }}</span>
                                        </el-button>
                                    </div>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column label="录制时间" width="118" align="center">
                            <template slot-scope="{ row }">
                                <div class="sol-time-cell">
                                    <div>{{ recordedAtLines(row.recordedAt).line1 }}</div>
                                    <div v-if="recordedAtLines(row.recordedAt).line2">{{ recordedAtLines(row.recordedAt).line2 }}</div>
                                </div>
                            </template>
                        </el-table-column>
                        <el-table-column prop="durationSeconds" label="时长" width="96" align="center"
                            :formatter="formatDurationCell"></el-table-column>
                        <el-table-column prop="fileSize" label="文件大小" width="100" align="center"
                            :formatter="formatFileSizeCell"></el-table-column>
                        <el-table-column label="操作" width="176" align="center" class-name="sol-op-col">
                            <template slot-scope="{ row }">
                                <div class="sol-video-actions">
                                    <el-button type="primary" size="small" @click="openPreview(row)">播放</el-button>
                                    <el-button type="primary" size="small" @click="downloadVideo(row)">下载</el-button>
                                </div>
                            </template>
                        </el-table-column>
                        <template slot="empty">
                            <el-empty description="该用户暂无录屏视频" />
                        </template>
                    </el-table>
                </div>
                <div v-show="videoTotal" class="sol-pagination">
                    <span class="sol-pagination-info">共 {{ videoTotal }} 条</span>
                    <pagination :total="videoTotal" :page.sync="videoPage" :limit.sync="videoPageSize"
                        :pageSizes="[10, 20, 30]" @pagination="onVideoPageChange" />
                </div>
            </div>
        </div>

        <el-dialog :visible.sync="playVisible" width="1000px" top="5vh" append-to-body custom-class="sol-play-dialog"
            @opened="startPlayback" @close="pausePlayback" @closed="currentPlay = null">
            <div slot="title" class="sol-play-head">
                <div class="sol-play-title">{{ playTitle }}</div>
                <div class="sol-play-info">{{ playInfo }}</div>
            </div>
            <div class="sol-record-preview">
                <video v-if="playSrc" ref="playVideo" class="sol-record-video" :src="playSrc" controls autoplay />
                <template v-else>
                    <i class="el-icon-video-play sol-record-play" />
                    <div class="sol-record-meta">录屏预览</div>
                </template>
            </div>
            <span slot="footer">
                <el-button type="primary" size="small" @click="downloadVideo(currentPlay)">下载</el-button>
                <el-button type="primary" size="small" @click="playVisible = false">关闭</el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
    import { mapState } from 'vuex';
    import { saveAs } from 'file-saver';
    import pagination from '@/common/community/pagination.vue';

    const EXPORT_POLL_MS = 2000;
    const EXPORT_TIMEOUT_MS = 12 * 60 * 1000;
    const COVER_FRAME = 20;
    const COVER_FPS = 30;
    const NAME_LINE_CHARS = 13;

    export default {
        name: 'SpaceOperationLog',
        components: { pagination },
        data() {
            return {
                logs: [],
                listTotal: 0,
                listLoading: false,
                spaceOptions: [],
                filterSpace: '',
                filterCount: '',
                filterSearch: '',
                listPage: 1,
                listPageSize: 10,
                currentUser: null,
                currentVideos: [],
                videoTotal: 0,
                videoLoading: false,
                videoPage: 1,
                videoPageSize: 10,
                playVisible: false,
                currentPlay: null,
                coverFailed: {},
                exporting: false,
                searchTimer: null,
                exportTimer: null,
            };
        },
        computed: {
            ...mapState({
                currentProject: (state) => state.user.currentProject,
            }),
            projectId() {
                return this.currentProject && this.currentProject.id;
            },
            playTitle() {
                return this.currentPlay ? (this.currentPlay.projectName || '预览录屏') : '预览录屏';
            },
            playSrc() {
                return this.getVideoSrc(this.currentPlay);
            },
            playInfo() {
                if (!this.currentPlay) return '—';
                const user = this.currentUser ? '｜用户：' + this.currentUser.userName : '';
                const duration = this.formatDuration(this.currentPlay.durationSeconds);
                const size = this.formatFileSize(this.currentPlay.fileSize);
                const resolution = this.currentPlay.resolution || '—';
                return '录制时间：' + (this.currentPlay.recordedAt || '—') + user
                    + '｜' + duration + '｜' + resolution + '｜' + size;
            },
            videoTableKey() {
                if (!this.currentUser) return 'video-table';
                return 'video-table-' + this.currentUser.userId + '-' + (this.currentUser.spaceId || '');
            },
        },
        watch: {
            projectId: {
                immediate: true,
                handler() {
                    this.currentUser = null;
                    this.listPage = 1;
                    this.fetchSpaceOptions();
                    this.fetchUserList();
                },
            },
        },
        beforeDestroy() {
            clearTimeout(this.searchTimer);
            this.clearExportTimer();
            this.pausePlayback();
        },
        methods: {
            isApiOk(res) {
                return res && (res.code === 200 || res.code === '200');
            },
            formatDuration(sec) {
                const n = Number(sec) || 0;
                const m = Math.floor(n / 60);
                const s = n % 60;
                return m + '分' + s + '秒';
            },
            formatFileSize(bytes) {
                if (bytes == null || bytes === '') return '—';
                const n = Number(bytes);
                if (!Number.isFinite(n) || n < 0) return '—';
                if (n < 1024) return n + ' B';
                const kb = n / 1024;
                if (kb < 1024) return kb.toFixed(1) + ' KB';
                return (n / (1024 * 1024)).toFixed(1) + ' MB';
            },
            formatDurationCell(row, column, cellValue) {
                return this.formatDuration(cellValue);
            },
            formatFileSizeCell(row, column, cellValue) {
                return this.formatFileSize(cellValue);
            },
            videoNameLines(name) {
                const text = name || '—';
                if (text.length <= NAME_LINE_CHARS) {
                    return { line1: text, line2: '' };
                }
                const line1 = text.slice(0, NAME_LINE_CHARS);
                if (text.length <= NAME_LINE_CHARS * 2) {
                    return { line1, line2: text.slice(NAME_LINE_CHARS) };
                }
                return { line1, line2: text.slice(NAME_LINE_CHARS, NAME_LINE_CHARS + 10) + '...' };
            },
            recordedAtLines(value) {
                const text = value == null ? '' : String(value).trim();
                if (!text) return { line1: '—', line2: '' };
                const matched = text.match(/^(\d{4})[-/.年](\d{1,2})[-/.月](\d{1,2})日?(?:\s+(\d{1,2}):(\d{2})(?::\d{2})?)?$/);
                if (!matched) return { line1: text, line2: '' };
                const pad = (part) => (part.length < 2 ? '0' + part : part);
                const line1 = matched[1] + '-' + pad(matched[2]) + '-' + pad(matched[3]);
                if (!matched[4]) return { line1, line2: '' };
                return { line1, line2: pad(matched[4]) + ':' + matched[5] };
            },
            isCoverFailed(row) {
                return !!(row && row.identifier && this.coverFailed[String(row.identifier)]);
            },
            markCoverFailed(row) {
                if (!row || !row.identifier) return;
                this.$set(this.coverFailed, String(row.identifier), true);
            },
            seekCoverFrame(event, row) {
                const video = event && event.target;
                if (!video || video.dataset.coverSeeked === '1') return;
                video.dataset.coverSeeked = '1';
                video.muted = true;
                const target = (COVER_FRAME - 1) / COVER_FPS;
                const duration = Number(video.duration);
                let time = target;
                if (Number.isFinite(duration) && duration > 0 && duration <= target) {
                    time = Math.max(0, duration - 0.05);
                }
                const onSeeked = () => {
                    video.pause();
                };
                video.addEventListener('seeked', onSeeked, { once: true });
                try {
                    video.pause();
                    video.currentTime = time;
                } catch (e) {
                    video.removeEventListener('seeked', onSeeked);
                    this.markCoverFailed(row);
                }
            },
            buildListQuery() {
                const query = {
                    projectId: this.projectId,
                    page: this.listPage,
                    limit: this.listPageSize,
                };
                if (this.filterSpace) query.spaceId = this.filterSpace;
                if (this.filterCount) query.countRange = this.filterCount;
                if (this.filterSearch) query.keyword = this.filterSearch;
                return query;
            },
            async fetchSpaceOptions() {
                if (!this.projectId) {
                    this.spaceOptions = [];
                    return;
                }
                try {
                    const res = await this.$Api.OperationLog.getSpaceOptions({ projectId: this.projectId });
                    if (!this.isApiOk(res)) {
                        this.spaceOptions = [];
                        return;
                    }
                    this.spaceOptions = Array.isArray(res.data) ? res.data : [];
                } catch (e) {
                    this.spaceOptions = [];
                }
            },
            async fetchUserList() {
                if (!this.projectId) {
                    this.logs = [];
                    this.listTotal = 0;
                    return;
                }
                this.listLoading = true;
                try {
                    const res = await this.$Api.OperationLog.getUserList(this.buildListQuery());
                    if (!this.isApiOk(res)) {
                        this.$message.error((res && res.msg) || '获取操作日志失败');
                        this.logs = [];
                        this.listTotal = 0;
                        return;
                    }
                    const data = res.data || {};
                    this.logs = Array.isArray(data.list) ? data.list : [];
                    this.listTotal = Number(data.totalCount) || 0;
                } catch (e) {
                    this.$message.error('获取操作日志失败');
                    this.logs = [];
                    this.listTotal = 0;
                } finally {
                    this.listLoading = false;
                }
            },
            async fetchVideos() {
                if (!this.projectId || !this.currentUser) {
                    this.currentVideos = [];
                    this.videoTotal = 0;
                    return;
                }
                this.videoLoading = true;
                try {
                    const res = await this.$Api.OperationLog.getVideos({
                        projectId: this.projectId,
                        userId: this.currentUser.userId,
                        spaceId: this.currentUser.spaceId,
                        page: this.videoPage,
                        limit: this.videoPageSize,
                    });
                    if (!this.isApiOk(res)) {
                        this.$message.error((res && res.msg) || '获取录屏明细失败');
                        this.currentVideos = [];
                        this.videoTotal = 0;
                        return;
                    }
                    const data = res.data || {};
                    this.coverFailed = {};
                    this.currentVideos = Array.isArray(data.list) ? data.list : [];
                    this.videoTotal = Number(data.totalCount) || 0;
                } catch (e) {
                    this.$message.error('获取录屏明细失败');
                    this.currentVideos = [];
                    this.videoTotal = 0;
                } finally {
                    this.videoLoading = false;
                }
            },
            onFilterChange() {
                this.listPage = 1;
                this.fetchUserList();
            },
            onSearchInput() {
                clearTimeout(this.searchTimer);
                this.searchTimer = setTimeout(() => {
                    this.onFilterChange();
                }, 400);
            },
            listIndexMethod(index) {
                return (this.listPage - 1) * this.listPageSize + index + 1;
            },
            videoIndexMethod(index) {
                return (this.videoPage - 1) * this.videoPageSize + index + 1;
            },
            onListPageChange({ pageIndex, pageSize }) {
                this.listPage = pageIndex || this.listPage;
                this.listPageSize = pageSize || this.listPageSize;
                this.fetchUserList();
            },
            onVideoPageChange({ pageIndex, pageSize }) {
                this.videoPage = pageIndex || this.videoPage;
                this.videoPageSize = pageSize || this.videoPageSize;
                this.fetchVideos();
            },
            showVideos(row) {
                this.currentUser = row;
                this.videoPage = 1;
                this.fetchVideos();
            },
            getVideoSrc(row) {
                if (!row || !row.identifier) return '';
                return this.downloadURL + encodeURIComponent(row.identifier);
            },
            openPreview(video) {
                this.currentPlay = video;
                this.playVisible = true;
            },
            startPlayback() {
                this.$nextTick(() => {
                    const video = this.$refs.playVideo;
                    if (!video || typeof video.play !== 'function') return;
                    const pending = video.play();
                    if (pending && typeof pending.catch === 'function') {
                        pending.catch(() => {});
                    }
                });
            },
            pausePlayback() {
                const video = this.$refs.playVideo;
                if (video && typeof video.pause === 'function') video.pause();
            },
            downloadVideo(video) {
                if (!video || !video.identifier) {
                    this.$message.warning('暂无可下载文件');
                    return;
                }
                const link = document.createElement('a');
                link.href = this.getVideoSrc(video);
                link.download = (video.projectName || '录屏') + '.webm';
                link.target = '_blank';
                link.click();
            },
            clearExportTimer() {
                if (this.exportTimer) {
                    clearInterval(this.exportTimer);
                    this.exportTimer = null;
                }
            },
            pollExport(taskId) {
                const started = Date.now();
                return new Promise((resolve, reject) => {
                    const tick = async () => {
                        if (Date.now() - started > EXPORT_TIMEOUT_MS) {
                            this.clearExportTimer();
                            reject(new Error('导出超时，请稍后重试'));
                            return;
                        }
                        try {
                            const res = await this.$Api.OperationLog.getExportStatus({ taskId });
                            if (!this.isApiOk(res)) {
                                this.clearExportTimer();
                                reject(new Error((res && res.msg) || '导出失败'));
                                return;
                            }
                            const data = res.data || {};
                            if (data.status === 'SUCCESS') {
                                this.clearExportTimer();
                                resolve(data.fileName);
                                return;
                            }
                            if (data.status === 'FAIL') {
                                this.clearExportTimer();
                                reject(new Error(data.errorMsg || '导出失败'));
                            }
                        } catch (e) {
                            this.clearExportTimer();
                            reject(e);
                        }
                    };
                    tick();
                    this.exportTimer = setInterval(tick, EXPORT_POLL_MS);
                });
            },
            async exportList() {
                if (!this.projectId) {
                    this.$message.warning('请先选择项目');
                    return;
                }
                this.exporting = true;
                try {
                    const res = await this.$Api.OperationLog.exportAsync({
                        projectId: this.projectId,
                        spaceId: this.filterSpace || null,
                        keyword: this.filterSearch || '',
                        countRange: this.filterCount || '',
                    });
                    if (!this.isApiOk(res)) {
                        this.$message.error((res && res.msg) || '导出失败');
                        return;
                    }
                    this.$message.success(res.msg || '导出任务已提交');
                    const fileName = await this.pollExport(res.data);
                    if (!fileName) {
                        this.$message.error('导出文件名无效');
                        return;
                    }
                    const blobRes = await this.$Api.OperationLog.exportDownload({ fileName });
                    const blob = blobRes && blobRes.data;
                    if (blob && blob.type && blob.type.indexOf('application/json') >= 0) {
                        this.$message.error('导出失败');
                        return;
                    }
                    saveAs(blob, fileName);
                } catch (e) {
                    this.$message.error((e && e.message) || '导出失败');
                } finally {
                    this.exporting = false;
                    this.clearExportTimer();
                }
            },
        },
    };
</script>

<style lang="less" scoped>
    @primary: #409EFF;
    @primary-light: #ECF5FF;
    @bg: #F5F7FA;
    @text-primary: #303133;
    @text-regular: #606266;
    @text-secondary: #909399;
    @border-light: #EBEEF5;
    @shadow-light: 0 2px 12px rgba(0, 0, 0, 0.08);
    @radius: 8px;

    .sol {
        min-height: 100%;
    }

    .sol-main {
        max-width: 1400px;
        margin: 0 auto;
        padding: 16px 20px 40px;
    }

    .sol-project-name-bar {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 16px;
        padding: 2px 0 4px;
    }

    .sol-project-name-icon {
        width: 42px;
        height: 42px;
        border-radius: 10px;
        background: @primary-light;
        color: @primary;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .sol-project-name-title {
        font-size: 18px;
        font-weight: 700;
        color: @text-primary;
        line-height: 1.45;
        margin: 0;
        letter-spacing: -0.02em;
        flex: 1;
        min-width: 0;
    }

    .sol-panel {
        background: #fff;
        border-radius: @radius;
        box-shadow: @shadow-light;
        overflow: hidden;
    }

    .sol-panel-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 14px 16px;
        border-bottom: 1px solid @border-light;
        flex-wrap: wrap;
        gap: 10px;
    }

    .sol-panel-title {
        font-size: 15px;
        font-weight: 600;
        color: @text-primary;
    }

    .sol-list-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 12px 16px;
        background: @bg;
        border-bottom: 1px solid @border-light;
        flex-wrap: wrap;

        > .el-button {
            flex-shrink: 0;
        }
    }

    .sol-filter-bar {
        display: flex;
        align-items: center;
        flex: 1;
        min-width: 0;
        margin-bottom: 0;
        flex-wrap: wrap;

        /deep/ .el-form-item {
            margin-bottom: 0;
            margin-right: 10px;
        }
    }

    .sol-panel-body {
        padding: 0;
        min-height: 200px;
    }

    .sol-pagination {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 12px;
        padding: 14px 16px;
        border-top: 1px solid @border-light;

        /deep/ .pagination {
            margin: 0;
        }
    }

    .sol-pagination-info {
        font-size: 13px;
        color: @text-secondary;
        line-height: 30px;
    }

    .sol-detail-header {
        justify-content: flex-start;
    }

    .sol-detail-meta {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px 18px;
        font-size: 13px;
        color: @text-regular;

        strong {
            color: @text-primary;
        }
    }

    .sol-video-name-cell {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .sol-video-cover {
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        min-width: 72px;
    }

    .sol-video-thumb {
        width: 72px;
        height: 40px;
        border-radius: 4px;
        background: #1a1a1a;
        color: #fff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;
        flex-shrink: 0;
        cursor: pointer;
        user-select: none;
        overflow: hidden;
    }

    .sol-cover-video {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        pointer-events: none;
        background: #000;
    }

    .sol-cover-resolution {
        margin-top: 2px;
        font-size: 12px;
        line-height: 1.2;
        color: @text-secondary;
        text-align: center;
        white-space: nowrap;
    }

    .sol-video-name-info {
        min-width: 0;
        flex: 1;

        /deep/ .el-button {
            height: auto;
            padding: 0;
            font-weight: 600;
            white-space: normal;
            text-align: left;
            line-height: 1.4;
        }
    }

    .sol-name-line {
        display: block;
        white-space: nowrap;
    }

    .sol-time-cell {
        line-height: 1.4;
    }

    .sol-video-actions {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-wrap: nowrap;
        gap: 8px;
        white-space: nowrap;

        .el-button + .el-button {
            margin-left: 0;
        }
    }

    .sol-record-preview {
        border-radius: 8px;
        background: #1a1a1a;
        color: #fff;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
        overflow: hidden;
    }

    .sol-play-head {
        padding-right: 28px;
    }

    .sol-play-title {
        font-size: 18px;
        line-height: 24px;
        color: @text-primary;
    }

    .sol-play-info {
        margin-top: 4px;
        font-size: 13px;
        color: @text-regular;
        line-height: 1.6;
        white-space: normal;
        word-break: break-word;
    }

    .sol-record-video {
        display: block;
        width: 100%;
        height: 100%;
        background: #000;
    }

    .sol-record-play {
        font-size: 48px;
        color: @primary;
    }

    .sol-record-meta {
        font-size: 12px;
        color: rgba(255, 255, 255, 0.75);
    }

    /deep/ .el-table td.sol-op-col .cell {
        overflow: visible;
        text-overflow: clip;
    }

    /deep/ .el-table {
        border: none;

        &::before {
            display: none;
        }
    }

    /deep/ .el-table__header thead th {
        color: #000;
        font-weight: 400;
        background-color: RGBA(250, 251, 255, 1);
    }

    /deep/ .el-table__empty-block {
        min-height: 200px;
    }
</style>

<style lang="less">
    .sol-play-dialog {
        .el-dialog__header {
            padding: 14px 20px 8px;
        }

        .el-dialog__headerbtn {
            top: 14px;
        }

        .el-dialog__body {
            padding: 8px 20px 0;
        }

        .el-dialog__footer {
            padding: 8px 20px 14px;
        }

        .sol-record-preview {
            height: 560px;
        }

        .el-dialog__footer .el-button + .el-button {
            margin-left: 8px;
        }
    }
</style>
